import { useEffect } from "react";
import { useLocation } from "react-router";

type RouteMeta = {
  title: string;
  description: string;
  noindex?: boolean;
};

const DEFAULT_META: RouteMeta = {
  title: "Leaderboard - Tutorship Dashboard",
  description: "Track your progress, compete on the leaderboard, and manage your tasks.",
};

const ROUTE_META: Record<string, RouteMeta> = {
  "/": DEFAULT_META,
  "/leaderboard": {
    title: "Leaderboard - Tutorship Dashboard",
    description: "Public leaderboard standings for the top participants and weekly point totals.",
  },
  "/login": {
    title: "Sign In - Tutorship Dashboard",
    description: "Sign in to your Tutorship account to access the dashboard and leaderboard.",
  },
  "/register": {
    title: "Create Account - Tutorship Dashboard",
    description: "Create a Tutorship account to start tracking your progress.",
  },
  "/dashboard": {
    title: "Dashboard - Tutorship",
    description: "View your progress, submissions, and activity on the Tutorship Dashboard.",
    noindex: true,
  },
  "/submit": {
    title: "Submit - Tutorship Dashboard",
    description: "Submit your work and track your progress on the Tutorship Dashboard.",
    noindex: true,
  },
  "/admin": {
    title: "Admin - Tutorship Dashboard",
    description: "Admin panel for managing the Tutorship Dashboard.",
    noindex: true,
  },
};

function setMetaContent(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setRobotsNoindex(noindex: boolean) {
  const existing = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (noindex) {
    if (existing) {
      existing.setAttribute("content", "noindex, nofollow");
    } else {
      const el = document.createElement("meta");
      el.setAttribute("name", "robots");
      el.setAttribute("content", "noindex, nofollow");
      document.head.appendChild(el);
    }
  } else if (existing) {
    existing.remove();
  }
}

export function setPageMeta(meta: RouteMeta): void {
  document.title = meta.title;
  setMetaContent("description", meta.description);
  setMetaContent("og:title", meta.title, "property");
  setMetaContent("og:description", meta.description, "property");
  setMetaContent("twitter:title", meta.title);
  setMetaContent("twitter:description", meta.description);
  setRobotsNoindex(meta.noindex === true);
}

export function PageMetaUpdater() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = ROUTE_META[pathname] ?? DEFAULT_META;
    setPageMeta(meta);
  }, [pathname]);

  return null;
}

export const PageTitleUpdater = PageMetaUpdater;
